import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import LinearProgress from "@mui/material/LinearProgress";
import { Button, Box, Typography, Stack, Chip, Card, CardContent, CardActions, Grid, Divider, Alert } from "@mui/material";
import { createTheme, ThemeProvider } from "@mui/material/styles";
import CssBaseline from "@mui/material/CssBaseline";
import { DirectionsCar, Repeat } from "@mui/icons-material";
import PhotoLibraryIcon from "@mui/icons-material/PhotoLibrary";
import Tooltip from "@mui/material/Tooltip";
import Pagination from "@mui/material/Pagination";
import StarHalfIcon from "@mui/icons-material/StarHalf";

const theme = createTheme({
  direction: "rtl",
  typography: {
    fontFamily: '"Cairo", "Tajawal", Arial, sans-serif',
  },
});

const API_BASE = "https://dash.tatabu.io/back-end/public/api/taxi-orders";

const formatPhoneForWhatsApp = (phone) => {
  if (!phone) return null;
  const cleaned = phone.replace(/\D/g, "");
  if (cleaned.length === 10 && cleaned.startsWith("05")) return "966" + cleaned.slice(1);
  if (cleaned.length === 9 && cleaned.startsWith("5")) return "966" + cleaned;
  if (cleaned.length === 12 && cleaned.startsWith("966")) return cleaned;
  return cleaned.startsWith("0") ? "966" + cleaned.slice(1) : cleaned;
};

const formatDate = (dateStr) => {
  if (!dateStr) return null;
  const d = new Date(dateStr);
  if (isNaN(d)) return dateStr;
  return d.toLocaleDateString("en-GB", { year: "numeric", month: "2-digit", day: "2-digit" });
};

const formatTime12h = (timeStr) => {
  if (!timeStr) return null;
  const [hourStr, minuteStr] = timeStr.split(":");
  let hour = parseInt(hourStr, 10);
  const minute = minuteStr || "00";
  const period = hour >= 12 ? "م" : "ص";
  hour = hour % 12 || 12;
  return `${hour}:${minute} ${period}`;
};

const statusMap = {
  ناقص: { label: "ناقص", color: "warning" },
  "مؤكد غير مدفوع": { label: "مؤكد غير مدفوع", color: "info" },
  "مؤكد مدفوع": { label: "مؤكد مدفوع", color: "success" },
  "تذكير أول": { label: "تذكير أول", color: "success" },
  "تذكير ثاني": { label: "تذكير ثاني", color: "success" },
  استبيان: { label: "استبيان", color: "success" },
  مكتمل: { label: "مكتمل", color: "success" },
  ملغي: { label: "ملغي", color: "error" },
};

export default function OrdersNeedingReturnTrip() {
  const navigate = useNavigate();
  //
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // Pagination
  const [page, setPage] = useState(1);
  const rowsPerPage = 12;
  const paginatedOrders = orders.slice((page - 1) * rowsPerPage, page * rowsPerPage);
  const handlePageChange = (_, value) => {
    setPage(value);
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  // Creating return trip state
  const [creatingId, setCreatingId] = useState(null);
  const [createError, setCreateError] = useState(null);
  const [createSuccess, setCreateSuccess] = useState(null);

  useEffect(() => {
    const fetchOrders = async () => {
      setLoading(true);
      setError(null);
      try {
        const res = await fetch(`${API_BASE}/needing-return-trip`);
        const json = await res.json();
        if (json.success) {
          setOrders(json.data.orders || []);
        } else {
          setError("فشل في تحميل البيانات");
        }
      } catch {
        setError("فشل الاتصال بالخادم");
      }
      setLoading(false);
    };
    fetchOrders();
  }, []);

  const handleOpenTicket = (row) => {
    if (!row.images || row.images.length === 0) {
      alert("لا توجد تذكرة طيران");
      return;
    }
    window.open(row.images[0].full_path, "_blank");
  };

  const handleCreateReturnTrip = async (order) => {
    setCreatingId(order.id);
    setCreateError(null);
    setCreateSuccess(null);
    try {
      const res = await fetch(`${API_BASE}/${order.id}/create-return-trip`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
      });
      const json = await res.json();
      if (json.success || res.ok) {
        setCreateSuccess(order.id);
        // Remove from list since it now has a return trip
        setOrders((prev) => prev.filter((o) => o.id !== order.id));
      } else {
        setCreateError({ id: order.id, message: json.message || "حدث خطأ" });
      }
    } catch {
      setCreateError({ id: order.id, message: "فشل الاتصال بالخادم" });
    }
    setCreatingId(null);
  };

  return (
    <ThemeProvider theme={theme}>
      <CssBaseline />
      <link href="https://fonts.googleapis.com/css2?family=Cairo:wght@400;600;700;800&display=swap" rel="stylesheet" />

      <Box dir="rtl">
        {loading && (
          <Box sx={{ position: "fixed", top: 0, left: 0, right: 0, zIndex: 9999 }}>
            <LinearProgress color="secondary" />
          </Box>
        )}

        {/* Page Header */}
        <Box
          sx={{
            px: { xs: 2, md: 4 },
            pt: 3,
            pb: 2,
            display: "flex",
            alignItems: "center",
            gap: 1.5,
            borderBottom: "1.5px solid #f0f0f0",
            backgroundColor: "#fff",
            mb: 3,
          }}
        >
          <Box sx={{ backgroundColor: "#e8f5e9", borderRadius: "12px", p: 1, display: "flex", alignItems: "center" }}>
            <StarHalfIcon sx={{ color: "#2e7d32", fontSize: 28 }} />
          </Box>
          <Box>
            <Typography sx={{ fontFamily: "Cairo, sans-serif", fontWeight: 800, fontSize: "20px", color: "#1a1a1a" }}>طلبات تحتاج رحلة عودة</Typography>
            <Typography sx={{ fontFamily: "Cairo, sans-serif", fontSize: "13px", color: "#999", fontWeight: 500 }}>
              {orders.length} طلب من نوع "ذهاب وعودة من جدة الى مكة" بدون رحلة عودة
            </Typography>
          </Box>
        </Box>

        {error && (
          <Box sx={{ px: 2, mb: 2 }}>
            <Alert severity="error" sx={{ fontFamily: "Cairo, sans-serif" }}>
              {error}
            </Alert>
          </Box>
        )}

        <Box sx={{ px: { xs: 1, sm: 2 }, pb: 4 }}>
          {/* Empty State */}
          {!loading && orders.length === 0 && (
            <Box display="flex" flexDirection="column" alignItems="center" justifyContent="center" sx={{ mt: 10, gap: 2 }}>
              <Typography sx={{ fontFamily: "Cairo, sans-serif", fontWeight: 700, fontSize: "18px", color: "#555" }}>جميع طلبات الذهاب والعودة لديها رحلة عودة</Typography>
            </Box>
          )}

          <Grid container spacing={2}>
            {paginatedOrders.map((row) => {
              const statusInfo = statusMap[row.new_status] || { label: row.new_status, color: "default" };
              const isCreating = creatingId === row.id;
              const hasError = createError?.id === row.id;

              return (
                <Grid item xs={12} sm={6} md={4} lg={3} key={row.id}>
                  <Card
                    elevation={2}
                    sx={{
                      borderRadius: 3,
                      height: "100%",
                      display: "flex",
                      flexDirection: "column",
                      fontFamily: '"Cairo", sans-serif',
                      border: "1px solid #c8e6c9",
                      transition: "box-shadow 0.2s",
                      "&:hover": { boxShadow: 6 },
                    }}
                  >
                    <CardContent sx={{ flex: 1, pb: 1 }}>
                      {/* Header: order number + ticket icon + status chip */}
                      <Stack direction="row" justifyContent="space-between" alignItems="center" mb={1.5}>
                        <Typography
                          sx={{
                            fontFamily: '"Cairo", sans-serif',
                            fontWeight: 800,
                            fontSize: "0.8rem",
                            color: "#888",
                            bgcolor: "#f5f5f5",
                            px: 1,
                            py: 0.3,
                            borderRadius: 1,
                          }}
                        >
                          {row.order_number}
                        </Typography>

                        <Stack direction="row" alignItems="center" gap={1}>
                          <Chip label={statusInfo.label} color={statusInfo.color} size="small" sx={{ fontFamily: '"Cairo", sans-serif', fontWeight: 600, fontSize: "0.75rem" }} />
                        </Stack>
                      </Stack>

                      <Box display="flex" justifyContent="space-between" alignItems="center" mb={0.5}>
                        {/* Client name */}
                        <Typography sx={{ fontFamily: '"Cairo", sans-serif', fontWeight: 800, fontSize: "1rem", color: "#1a1a1a", mb: 0.5 }}>{row.name || "—"}</Typography>

                        <div style={{ display: "flex", gap: "6px", alignItems: "center" }}>
                          {!row.has_return_trip && !row.is_return_trip && row.type === "ذهاب وعودة من جدة الى مكة" && (
                            <Tooltip title="طلب ذهاب وعودة لم يتم اضافة (جدولة) رحلة العودة الخاصة به">
                              <StarHalfIcon sx={{ color: "#1a1a1a", cursor: "pointer" }} />
                            </Tooltip>
                          )}

                          {row.images && row.images.length > 0 && (
                            <Tooltip title="تذكرة الطيران">
                              <PhotoLibraryIcon onClick={() => handleOpenTicket(row)} sx={{ fontSize: 20, cursor: "pointer", color: "#222", "&:hover": { color: "#0d47a1" } }} />
                            </Tooltip>
                          )}
                        </div>
                      </Box>

                      {/* Phone */}
                      {row.phone && (
                        <Box mb={1.2} dir="ltr" sx={{ textAlign: "right" }}>
                          <a
                            href={`https://wa.me/${formatPhoneForWhatsApp(row.phone)}`}
                            target="_blank"
                            rel="noopener noreferrer"
                            style={{
                              color: "#25D366",
                              textDecoration: "none",
                              fontWeight: 700,
                              fontSize: "13px",
                              fontFamily: "Cairo, sans-serif",
                              backgroundColor: "rgba(37,211,102,0.1)",
                              padding: "3px 10px",
                              borderRadius: "8px",
                              display: "inline-block",
                            }}
                          >
                            {row.phone}
                          </a>
                        </Box>
                      )}

                      {/* Return trip badge */}
                      {/* <Box mb={1}>
                        <Chip
                          label="ذهاب وعودة من جدة الى مكة"
                          size="small"
                          sx={{
                            fontFamily: '"Cairo", sans-serif',
                            fontSize: "0.7rem",
                            fontWeight: 700,
                            bgcolor: "#e8f5e9",
                            color: "#2e7d32",
                            borderRadius: "6px",
                            height: "22px",
                            "& .MuiChip-icon": { color: "#2e7d32" },
                          }}
                        />
                      </Box> */}

                      {/* type rows */}
                      <Stack direction="row" alignItems="center" gap={0.8} mb={0.8}>
                        <Typography sx={{ fontFamily: '"Cairo", sans-serif', fontSize: "0.75rem", color: "#999", minWidth: 60 }}>الخدمة:</Typography>
                        <Stack direction="row" alignItems="center" gap={0.5}>
                          <Typography sx={{ fontFamily: '"Cairo", sans-serif', fontWeight: 600, fontSize: "0.88rem", color: "#333" }}>{row.type || "—"}</Typography>
                        </Stack>
                      </Stack>

                      {/* Info rows */}
                      <Stack direction="row" alignItems="center" gap={0.8} mb={0.8}>
                        <Typography sx={{ fontFamily: '"Cairo", sans-serif', fontSize: "0.75rem", color: "#999", minWidth: 60 }}>السيارة:</Typography>
                        <Stack direction="row" alignItems="center" gap={0.5}>
                          <DirectionsCar sx={{ fontSize: 16, color: "#1565c0" }} />
                          <Typography sx={{ fontFamily: '"Cairo", sans-serif', fontWeight: 600, fontSize: "0.88rem", color: "#333" }}>{row.car_model || "—"}</Typography>
                        </Stack>
                      </Stack>

                      {row.pickup_date && (
                        <Stack direction="row" alignItems="center" gap={0.8} mb={0.8}>
                          <Typography sx={{ fontFamily: '"Cairo", sans-serif', fontSize: "0.75rem", color: "#999", minWidth: 60 }}>الاستقبال:</Typography>
                          <Typography sx={{ fontFamily: '"Cairo", sans-serif', fontWeight: 600, fontSize: "0.85rem", color: "#333" }}>
                            {formatDate(row.pickup_date)}
                            {row.pickup_hour ? ` — ${formatTime12h(row.pickup_hour)}` : ""}
                          </Typography>
                        </Stack>
                      )}

                      <Stack direction="row" alignItems="center" gap={0.8} mb={0.8}>
                        <Typography sx={{ fontFamily: '"Cairo", sans-serif', fontSize: "0.75rem", color: "#999", minWidth: 60 }}>الموقع:</Typography>
                        <Typography sx={{ fontFamily: '"Cairo", sans-serif', fontWeight: 600, fontSize: "0.85rem", color: "#333" }}>{row.pickup_location || "—"}</Typography>
                      </Stack>

                      <Stack direction="row" alignItems="center" gap={0.8} mb={0.8}>
                        <Typography sx={{ fontFamily: '"Cairo", sans-serif', fontSize: "0.75rem", color: "#999", minWidth: 60 }}>الإجمالي:</Typography>
                        <Typography sx={{ fontFamily: '"Cairo", sans-serif', fontWeight: 700, fontSize: "0.88rem", color: "#d33030" }}>{row.price} ﷼</Typography>
                      </Stack>

                      {hasError && (
                        <Alert severity="error" sx={{ mt: 1, fontFamily: '"Cairo", sans-serif', fontSize: "0.78rem", py: 0 }}>
                          {createError.message}
                        </Alert>
                      )}
                    </CardContent>

                    <CardActions sx={{ px: 2, py: 1.5 }}>
                      <Button
                        fullWidth
                        variant="contained"
                        size="small"
                        onClick={() => navigate(`/dashboard/taxi/add-return-trip/${row?.id}`)}
                        sx={{
                          fontFamily: '"Cairo", sans-serif',
                          fontWeight: 700,
                          borderRadius: 2,
                          fontSize: "0.82rem",
                          py: 0.8,
                          bgcolor: "#2e7d32",
                          "&:hover": { bgcolor: "#1b5e20" },
                        }}
                      >
                        إنشاء رحلة العودة
                      </Button>
                    </CardActions>
                  </Card>
                </Grid>
              );
            })}
          </Grid>
        </Box>

        {/* Pagination */}
        {orders.length > rowsPerPage && (
          <Box display="flex" justifyContent="center" alignItems="center" mt={2} mb={4}>
            <Pagination count={Math.ceil(orders.length / rowsPerPage)} page={page} onChange={handlePageChange} color="primary" shape="rounded" hidePrevButton hideNextButton />
          </Box>
        )}
      </Box>
    </ThemeProvider>
  );
}
